import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import LabelPreview from './LabelPreview';
import { getPreferredPrinterConfig, setPreferredPrinterConfig } from '../../utils/printerPreferences';
import labelService from '../../services/labelService';

const LABEL_TEMPLATE_ID = 'sample-label';

const parseTemplateDocument = (html) => {
  if (!html || typeof DOMParser === 'undefined') {
    return { head: '', body: '' };
  }
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return {
      head: doc.head ? doc.head.innerHTML : '',
      body: doc.body ? doc.body.innerHTML : '',
    };
  } catch (err) {
    console.error('[LabelComposer] Failed to parse template HTML', err);
    return { head: '', body: '' };
  }
};

const parsePositiveInt = (value, fallback = 1) => {
  const numeric = parseInt(value, 10);
  if (Number.isNaN(numeric) || numeric <= 0) {
    return fallback;
  }
  return Math.min(numeric, 50);
};

const resolveProductPrice = (product) => {
  if (!product) return 0;
  if (product.price != null) return Number(product.price);
  if (product.storePriceOverride?.price != null) return Number(product.storePriceOverride.price);
  if (product.basePrice != null) return Number(product.basePrice);
  return 0;
};

const buildInitialForm = (product, currencySymbol) => ({
  name: product?.name || '',
  barcode: product?.barcode || product?.sku || '',
  price: Number(resolveProductPrice(product)).toFixed(2),
  unit: 'ea',
  note: '',
  currencySymbol,
});

const LabelComposer = ({
  product,
  store,
  onClose,
  variant = 'embedded',
  heading = 'Print label',
  actionLabel = 'Print labels',
  onPrinted,
  autoPrint = false,
}) => {
  const initialCurrency = store?.currencySymbol || '$';
  const [formData, setFormData] = useState(buildInitialForm(product, initialCurrency));
  const [debouncedFormData, setDebouncedFormData] = useState(buildInitialForm(product, initialCurrency));
  const [copies, setCopies] = useState(1);
  const [error, setError] = useState('');
  const [isPrinting, setIsPrinting] = useState(false);
  const autoPrintSignatureRef = useRef(null);
  const labelRef = useRef(null);
  const [renderedLabel, setRenderedLabel] = useState({ rawHtml: '', head: '', body: '', data: null });
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState('');

  const priceNumber = useMemo(() => Number(formData.price || 0), [formData.price]);

  useEffect(() => {
    if (!product) {
      return;
    }
    const nextForm = buildInitialForm(product, store?.currencySymbol || '$');
    setFormData(nextForm);
    setDebouncedFormData(nextForm);
    setCopies(1);
    setError('');
  }, [product, store?.currencySymbol]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedFormData(formData);
    }, 250);
    return () => clearTimeout(handle);
  }, [formData]);

  useEffect(() => {
    if (!product && !debouncedFormData.name?.trim()) {
      setRenderedLabel({ rawHtml: '', head: '', body: '', data: null });
      setPreviewHtml('');
      return;
    }
    let isActive = true;
    const fetchTemplate = async () => {
      setIsTemplateLoading(true);
      setTemplateError('');
      try {
        const response = await labelService.renderLabel({
          templateId: LABEL_TEMPLATE_ID,
          productId: product?._id,
          data: {
            name: debouncedFormData.name,
            price: Number(debouncedFormData.price) || 0,
            unit: debouncedFormData.unit || 'ea',
            barcodeNumber: debouncedFormData.barcode,
            lineTwo: debouncedFormData.note,
          },
        });
        if (!isActive) {
          return;
        }
        const parsed = parseTemplateDocument(response.html);
        setRenderedLabel({
          rawHtml: response.html,
          head: parsed.head,
          body: parsed.body,
          data: response.data,
        });
        setPreviewHtml(response.html);
      } catch (err) {
        if (!isActive) {
          return;
        }
        console.error('[LabelComposer] Unable to render label template', err);
        const message = err?.response?.data?.message || 'Unable to render label template.';
        setTemplateError(message);
        setRenderedLabel({ rawHtml: '', head: '', body: '', data: null });
        setPreviewHtml('');
      } finally {
        if (isActive) {
          setIsTemplateLoading(false);
        }
      }
    };
    fetchTemplate();
    return () => {
      isActive = false;
    };
  }, [
    product,
    debouncedFormData.name,
    debouncedFormData.price,
    debouncedFormData.unit,
    debouncedFormData.barcode,
    debouncedFormData.note,
  ]);

  const handleFieldChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const buildFallbackMarkup = useCallback(() => {
    if (!labelRef.current) {
      return null;
    }
    return labelRef.current.outerHTML;
  }, []);

  const buildPrintableHtml = useCallback(() => {
    const labelMarkup = renderedLabel.body || buildFallbackMarkup();
    if (!labelMarkup) {
      return null;
    }
    const labelCount = parsePositiveInt(copies, 1);
    const repeated = Array.from({ length: labelCount })
      .map(() => labelMarkup)
      .join('');
    const wrapperStyles = `
          body { margin: 0; padding: 20px; background: #fff; }
          .label-print-grid { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-start; justify-content: flex-start; text-align: center; }
          .label-print-item { page-break-inside: avoid; break-inside: avoid; display: block; }
        `;

    return `<!DOCTYPE html>
<html>
      <head>
        ${renderedLabel.head || ''}
        <style>${wrapperStyles}</style>
      </head>
      <body>
        <div id="receipt-to-print" class="label-print-grid">${repeated}</div>
      </body>
    </html>`;
  }, [buildFallbackMarkup, copies, renderedLabel.body, renderedLabel.head]);

  const getPrinterPlugin = () => {
    if (!Capacitor.isPluginAvailable('ReceiptPrinter')) {
      return null;
    }
    return window?.Capacitor?.Plugins?.ReceiptPrinter || null;
  };

  const [printerSettings, setPrinterSettings] = useState(() => {
    const cfg = getPreferredPrinterConfig('label');
    return { ...cfg, port: String(cfg.port || 9100) };
  });
  const [printerPrefsMessage, setPrinterPrefsMessage] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');

  const tryNativePrint = useCallback(async (html) => {
    const plugin = getPrinterPlugin();
    if (!plugin) {
      return { usedNative: false, error: null };
    }
    try {
      const payload = { html, jobType: 'label' };
      const printerConfig = getPreferredPrinterConfig('label');
      if (printerConfig.transport === 'network' && printerConfig.host) {
        payload.networkHost = printerConfig.host;
        if (printerConfig.port) {
          payload.networkPort = printerConfig.port;
        }
      } else if (printerConfig.bluetoothName) {
        payload.bluetoothNameHint = printerConfig.bluetoothName;
      }
      await plugin.print(payload);
      return { usedNative: true, error: null };
    } catch (err) {
      console.error('[LabelComposer] Native print failed', err);
      return { usedNative: false, error: err };
    }
  }, []);

  const openBrowserPrint = useCallback((html) => {
    const printWindow = window.open('', '_blank', 'width=600,height=800');
    if (!printWindow) {
      window.print();
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 200);
  }, []);

  const startPrint = useCallback(async () => {
    setError('');
    const printableHtml = buildPrintableHtml();
    if (!printableHtml) {
      setError('Label template is not ready yet.');
      return;
    }

    setIsPrinting(true);
    try {
      const { usedNative, error } = await tryNativePrint(printableHtml);
      if (error) {
        setError(error.message || 'Printer not available.');
        return;
      }
      if (!usedNative) {
        openBrowserPrint(printableHtml);
      }
      setFormData(buildInitialForm(null, store?.currencySymbol || '$'));
      setCopies(1);
      if (typeof onPrinted === 'function') {
        onPrinted();
      }
    } catch (err) {
      console.error('[LabelComposer] Print failed', err);
      setError('Unable to send label to printer.');
    } finally {
      setIsPrinting(false);
    }
  }, [buildPrintableHtml, onPrinted, openBrowserPrint, store?.currencySymbol, tryNativePrint]);

  const productFormMatches = useMemo(() => {
    if (!product) {
      return false;
    }
    const expected = buildInitialForm(product, store?.currencySymbol || '$');
    return (
      formData.name === expected.name &&
      formData.barcode === expected.barcode &&
      formData.price === expected.price &&
      formData.currencySymbol === expected.currencySymbol
    );
  }, [product, store?.currencySymbol, formData]);

  useEffect(() => {
    if (!autoPrint || !productFormMatches || isPrinting || !renderedLabel.body) {
      return;
    }
    const signature = `${formData.name}|${formData.barcode}|${formData.price}`;
    if (autoPrintSignatureRef.current === signature) {
      return;
    }
    autoPrintSignatureRef.current = signature;
    startPrint();
  }, [
    autoPrint,
    productFormMatches,
    isPrinting,
    formData.name,
    formData.barcode,
    formData.price,
    startPrint,
    renderedLabel.body,
  ]);

  useEffect(() => {
    if (!product) {
      autoPrintSignatureRef.current = null;
    }
  }, [product]);

  if (!product) {
    return (
      <div className="bg-white rounded-2xl shadow p-6 text-center text-gray-500">
        Select a product to begin composing a label.
      </div>
    );
  }

  const hasTemplateMarkup = Boolean(renderedLabel.body);
  const hasFallbackMarkup = Boolean(labelRef.current);
  const disablePrint =
    !formData.name.trim() ||
    Number.isNaN(priceNumber) ||
    (isTemplateLoading && !hasTemplateMarkup && !hasFallbackMarkup);
  const handleSavePrinterSettings = () => {
    setPreferredPrinterConfig('label', {
      ...printerSettings,
      port: Number(printerSettings.port) || undefined,
    });
    const refreshed = getPreferredPrinterConfig('label');
    setPrinterSettings({
      ...refreshed,
      port: String(refreshed.port || 9100),
    });
    setPrinterPrefsMessage('Printer preferences saved.');
    setTimeout(() => setPrinterPrefsMessage(''), 2500);
  };

  const containerClasses =
    variant === 'modal'
      ? 'bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-2rem)] overflow-y-auto'
      : 'bg-white rounded-2xl shadow p-4 sm:p-6';

  return (
    <>
    <div className={containerClasses}>
      <div className={variant === 'modal' ? 'flex flex-col md:flex-row' : 'flex flex-col md:flex-row'}>
        <div className="flex-1 p-4 md:p-6 border-b md:border-b-0 md:border-r border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">{heading}</h2>
          <p className="text-sm text-gray-500 mb-6">
            {store?.name ? `Store: ${store.name}` : 'Select store to reflect correct pricing.'}
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product name</label>
              <input
                type="text"
                value={formData.name}
                onChange={handleFieldChange('name')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price ({formData.currencySymbol})</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={handleFieldChange('price')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit suffix</label>
                <input
                  type="text"
                  value={formData.unit}
                  onChange={handleFieldChange('unit')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  maxLength={6}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Barcode</label>
              <input
                type="text"
                value={formData.barcode}
                onChange={handleFieldChange('barcode')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lower caption (optional)</label>
              <input
                type="text"
                value={formData.note}
                onChange={handleFieldChange('note')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Season, variant, etc."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Number of copies</label>
              <input
                type="number"
                min="1"
                max="50"
                value={copies}
                onChange={(event) => setCopies(parsePositiveInt(event.target.value, 1))}
                className="w-32 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

          <div className="mt-6 border border-gray-200 rounded-2xl p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">Printer preferences</p>
                <p className="text-xs text-gray-500">
                  Choose whether this device prints via Bluetooth or a LAN-connected printer.
                </p>
              </div>
              <button
                type="button"
                onClick={handleSavePrinterSettings}
                className="px-4 py-2 rounded-full bg-gray-900 text-white text-sm"
              >
                Save
              </button>
            </div>
            {printerPrefsMessage && (
              <p className="text-xs text-green-600 mb-2">{printerPrefsMessage}</p>
            )}
            <div className="flex flex-wrap gap-4 text-sm mb-3">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="composerPrinterTransport"
                  value="bluetooth"
                  checked={printerSettings.transport === 'bluetooth'}
                  onChange={() =>
                    setPrinterSettings((prev) => ({ ...prev, transport: 'bluetooth' }))
                  }
                />
                Bluetooth
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="composerPrinterTransport"
                  value="network"
                  checked={printerSettings.transport === 'network'}
                  onChange={() =>
                    setPrinterSettings((prev) => ({ ...prev, transport: 'network' }))
                  }
                />
                Network (LAN)
              </label>
            </div>
            {printerSettings.transport === 'network' ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input
                  type="text"
                  value={printerSettings.host || ''}
                  onChange={(e) =>
                    setPrinterSettings((prev) => ({ ...prev, host: e.target.value }))
                  }
                  placeholder="Printer IP address"
                  className="sm:col-span-2 border border-gray-300 rounded-xl px-3 py-2"
                />
                <input
                  type="number"
                  min="1"
                  max="65535"
                  value={printerSettings.port || ''}
                  onChange={(e) =>
                    setPrinterSettings((prev) => ({ ...prev, port: e.target.value }))
                  }
                  placeholder="Port"
                  className="border border-gray-300 rounded-xl px-3 py-2"
                />
              </div>
            ) : (
              <input
                type="text"
                value={printerSettings.bluetoothName || ''}
                onChange={(e) =>
                  setPrinterSettings((prev) => ({ ...prev, bluetoothName: e.target.value }))
                }
                placeholder="Bluetooth printer name"
                className="w-full border border-gray-300 rounded-xl px-3 py-2"
              />
            )}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            {!autoPrint && (
              <button
                onClick={startPrint}
                disabled={disablePrint || isPrinting}
                className={`px-5 py-2 rounded-full text-white font-semibold ${
                  disablePrint || isPrinting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isPrinting ? 'Sending…' : actionLabel}
              </button>
            )}
            {onClose && (
              <button
                onClick={onClose}
                className="px-5 py-2 rounded-full border border-gray-300 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        <div className="bg-gray-50 flex-1 min-h-[260px] p-6 flex items-center justify-center">
          <div className="w-full flex flex-col items-center">
            <p className="text-sm uppercase tracking-[0.3em] text-gray-500 text-center mb-4">Preview</p>
            <div className="bg-white rounded-xl shadow-md p-4 min-h-[180px] flex items-center justify-center w-full max-w-md">
              {isTemplateLoading ? (
                <p className="text-sm text-gray-500">Generating preview…</p>
              ) : previewHtml ? (
                <iframe
                  title="Label preview"
                  srcDoc={previewHtml}
                  className="w-full max-w-[320px] h-[170px] border-0"
                />
              ) : (
                <LabelPreview
                  name={formData.name}
                  barcode={formData.barcode}
                  price={priceNumber}
                  unit={(formData.unit || 'ea').toUpperCase()}
                  currencySymbol={formData.currencySymbol}
                  note={formData.note}
                />
              )}
            </div>
            {templateError && (
              <p className="text-xs text-red-600 text-center mt-3">{templateError}</p>
            )}
          </div>
        </div>
      </div>
    </div>
    <div
      style={{ position: 'absolute', left: '-10000px', top: 0, height: 0, overflow: 'hidden' }}
      aria-hidden="true"
    >
      <div ref={labelRef}>
        <LabelPreview
          name={formData.name}
          barcode={formData.barcode}
          price={priceNumber}
          unit={(formData.unit || 'ea').toUpperCase()}
          currencySymbol={formData.currencySymbol}
          note={formData.note}
        />
      </div>
    </div>
    </>
  );
};

export default LabelComposer;
